import apiService from '../../../services/api-client';

// Si el backend devuelve un 422 (validacion de FastAPI/Pydantic), `detail`
// es un array de {type, loc, msg, input} -- no un string. Renderizar eso
// directo en JSX revienta React ("Objects are not valid as a React
// child"), asi que todo el modulo pasa por esto antes de mostrar un error.
export const extractApiErrorMessage = (err: any, fallback: string): string => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  if (Array.isArray(detail) && detail.length > 0) {
    const messages = detail
      .map((item: any) => (item && typeof item === 'object' ? item.msg : String(item)))
      .filter(Boolean);
    if (messages.length > 0) {
      return messages.join(' — ');
    }
  }
  return fallback;
};

// Tipos del modulo Stock Comparison. Coinciden 1:1 con StockComparison*Schemas
// en el backend (schemas/stock_comparison_schemas.py), que ya exponen
// camelCase via alias_generator=to_camel -- no hace falta traducir nada aca.

export interface StockComparisonConfig {
  stockComparisonEnabled: boolean;
  secondarySystemLabel: string | null;
  varianceThresholdPct: string;
}

// Cada campo es independiente y opcional: undefined = no tocar lo que ya
// estaba guardado. stockComparisonEnabled undefined = no tocarlo (el
// backend lo rechaza igual si quien llama no es superadmin).
export interface StockComparisonConfigUpdate {
  stockComparisonEnabled?: boolean;
  secondarySystemLabel?: string | null;
  varianceThresholdPct?: number | string;
}

export interface StockComparisonUploadItem {
  productSku: string;
  warehouseLabel: string;
  quantity: number | string;
  // Si no viene, el backend asume la fecha de hoy (momento de la carga).
  snapshotDate?: string;
}

export type StockComparisonResultStatus = 'matched' | 'deviated' | 'missing_secondary' | 'missing_primary';

export interface StockComparisonResultItem {
  productSku: string;
  productName: string | null;
  warehouseLabel: string;
  primaryQty: string | null;
  secondaryQty: string | null;
  deviationQty: string | null;
  deviationPct: string | null;
  deviationValue: string | null;
  status: StockComparisonResultStatus;
}

export interface StockComparisonUploadResponse {
  comparisonDate: string;
  totalRows: number;
  matchedCount: number;
  deviatedCount: number;
  missingPrimaryCount: number;
  results: StockComparisonResultItem[];
}

export interface StockComparisonTrendPoint {
  comparisonDate: string;
  totalRows: number;
  matchedCount: number;
  deviatedCount: number;
  missingPrimaryCount: number;
  avgDeviationPct: string | null;
  maxDeviationPct: string | null;
  totalDeviationValue: string | null;
}

export interface StockComparisonTrendResponse {
  points: StockComparisonTrendPoint[];
}

export interface StockComparisonMovementItem {
  movementDate: string;
  typeLabel: string;
  quantity: string | null;
  reference: string | null;
  notes: string | null;
}

export type StockComparisonMovementsSource = 'bsale' | 'skualo' | 'generic' | null;

export interface StockComparisonMovementsResponse {
  source: StockComparisonMovementsSource;
  productSku: string;
  warehouseLabel: string;
  rangeStart: string;
  rangeEnd: string;
  movements: StockComparisonMovementItem[];
}

export const stockComparisonService = {
  async getConfig(companyId: number) {
    const { data } = await apiService().get<StockComparisonConfig>(`/stock-comparison/${companyId}/config`);
    return data;
  },

  async updateConfig(companyId: number, updates: StockComparisonConfigUpdate) {
    const { data } = await apiService().put<StockComparisonConfig>(`/stock-comparison/${companyId}/config`, updates);
    return data;
  },

  async uploadFile(companyId: number, items: StockComparisonUploadItem[], sourceFileName?: string) {
    const { data } = await apiService().post<StockComparisonUploadResponse>(`/stock-comparison/${companyId}/upload`, {
      items,
      sourceFileName,
    });
    return data;
  },

  async getTrend(companyId: number, days = 30) {
    const { data } = await apiService().get<StockComparisonTrendResponse>(`/stock-comparison/${companyId}/trend`, {
      params: { days },
    });
    return data;
  },

  async getMovements(
    companyId: number,
    params: { productSku: string; warehouseLabel: string; referenceDate: string; daysBack?: number; daysForward?: number },
  ) {
    const { data } = await apiService().get<StockComparisonMovementsResponse>(`/stock-comparison/${companyId}/movements`, {
      params,
    });
    return data;
  },
};
